"use client";

import { useState } from "react";
import Link from "next/link";
import { Check } from "lucide-react";

const PLANS = [
  {
    name: "Starter",
    price: { monthly: 49, yearly: 39 },
    desc: "For small teams getting started with structured HR.",
    features: [
      "Up to 50 employees",
      "Attendance & Leave",
      "Employee directory",
      "Email support",
    ],
    cta: "Start free trial",
    highlighted: false,
  },
  {
    name: "Growth",
    price: { monthly: 99, yearly: 79 },
    desc: "For growing companies that need payroll and performance.",
    features: [
      "Up to 250 employees",
      "Everything in Starter",
      "Payroll & compliance",
      "Performance reviews",
      "Priority support",
    ],
    cta: "Start free trial",
    highlighted: true,
  },
  {
    name: "Enterprise",
    price: { monthly: null, yearly: null },
    desc: "For large organizations with custom requirements.",
    features: [
      "Unlimited employees",
      "Everything in Growth",
      "Recruitment (ATS)",
      "Custom integrations",
      "Dedicated account manager",
    ],
    cta: "Contact sales",
    highlighted: false,
  },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(true);

  return (
    <section id="pricing-section" className="py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-lg text-muted-foreground">
            Choose a plan that grows with your team. No hidden fees.
          </p>
        </div>

        <div className="flex items-center justify-center gap-3 mb-14">
          <span
            className={`text-sm font-medium ${!yearly ? "text-foreground" : "text-muted-foreground"}`}
          >
            Monthly
          </span>
          {/* <button
            onClick={() => setYearly(!yearly)}
            className="relative w-13 h-7 rounded-full bg-muted border border-border transition-colors shrink-0"
            aria-label="Toggle billing period"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-primary transition-transform duration-200 ${
                yearly ? "translate-x-6" : "translate-x-0"
              }`}
            />
          </button> */}
          <button
            type="button"
            className="relative inline-flex h-6 w-11 items-center rounded-full bg-muted border border-border transition-colors shrink-0"
            onClick={() => setYearly(!yearly)}
            aria-label="Toggle billing period"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-primary transition-transform duration-200 ${
                yearly ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
          <span
            className={`text-sm font-medium ${yearly ? "text-foreground" : "text-muted-foreground"}`}
          >
            Yearly
          </span>
          <span className="text-xs font-medium text-primary bg-primary/10 rounded-full px-2.5 py-1 whitespace-nowrap">
            Save 20%
          </span>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-xl border p-8 flex flex-col ${
                plan.highlighted
                  ? "border-primary bg-card shadow-lg"
                  : "border-border bg-card"
              }`}
            >
              {plan.highlighted && (
                <span className="absolute -top-3 left-8 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                  Most popular
                </span>
              )}

              <h3 className="text-lg font-semibold text-foreground mb-1">
                {plan.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">{plan.desc}</p>

              <div className="mb-6">
                {plan.price.monthly ? (
                  <>
                    <span className="text-4xl font-bold text-foreground">
                      ${yearly ? plan.price.yearly : plan.price.monthly}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {" "}
                      /employee/mo
                    </span>
                  </>
                ) : (
                  <span className="text-4xl font-bold text-foreground">
                    Custom
                  </span>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2.5 text-sm text-foreground"
                  >
                    <Check size={16} className="text-primary mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                href={plan.name === "Enterprise" ? "/contact" : "/book-demo"}
                className={`text-center text-sm font-semibold rounded-lg px-5 py-3 transition-colors ${
                  plan.highlighted
                    ? "bg-primary text-primary-foreground hover:opacity-90"
                    : "border border-border text-foreground hover:bg-muted"
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
